import type { CgOverlayProgramScope, CgOverlayProgramTarget, CgOverlayTargetKind } from '../../types/index.js'

/**
 * Ciblage par programme de l'onglet Diffusion. Le moteur decide avec
 * `backend/src/lib/cg-overlay-program-scope.ts` ; ce module ne fait que
 * resumer et preparer ce que l'operateur choisit.
 */

export const SCOPE_LABELS: Record<CgOverlayProgramScope, string> = {
  ALL: 'Tous les programmes',
  INCLUDE: 'Uniquement…',
  EXCLUDE: 'Tous sauf…',
}

export const TARGET_KIND_LABELS: Record<CgOverlayTargetKind, string> = {
  SERIES: 'émission',
  PROGRAM: 'fiche',
  CATEGORY: 'catégorie',
}

function targetList(targets: CgOverlayProgramTarget[]): string {
  return targets.map(target => `${target.label} (${TARGET_KIND_LABELS[target.kind]})`).join(', ')
}

export function cgOverlayScopeSummary(scope: CgOverlayProgramScope, targets: CgOverlayProgramTarget[]): string {
  if (scope === 'ALL') return 'S\'affiche sur tous les programmes et directs.'
  if (targets.length === 0) {
    return scope === 'INCLUDE'
      ? 'Ajoutez au moins une émission, une fiche ou une catégorie : sans cible, l\'habillage ne sortirait jamais.'
      : 'Ajoutez au moins une émission, une fiche ou une catégorie à écarter.'
  }
  return scope === 'INCLUDE'
    ? `S'affiche uniquement sur : ${targetList(targets)}.`
    : `S'affiche partout sauf sur : ${targetList(targets)}.`
}

export function cgOverlayScopeListLabel(scope: CgOverlayProgramScope, count: number): string {
  if (scope === 'ALL') return 'Tous les programmes'
  const targets = `${count} cible${count > 1 ? 's' : ''}`
  return scope === 'INCLUDE' ? `Uniquement ${targets}` : `Sauf ${targets}`
}

export function cgOverlayTargetsPayload(scope: CgOverlayProgramScope, targets: CgOverlayProgramTarget[]) {
  const ids = (kind: CgOverlayTargetKind) => scope === 'ALL'
    ? []
    : targets.filter(target => target.kind === kind).map(target => target.id)
  return {
    programScope: scope,
    targetSeriesIds: ids('SERIES'),
    targetProgramIds: ids('PROGRAM'),
    targetCategoryIds: ids('CATEGORY'),
  }
}

function fold(value: string): string {
  return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
}

/** Accents et casse ignores, tous les mots requis. */
export function matchesCatalogSearch(label: string, search: string): boolean {
  const words = fold(search).split(/\s+/).filter(Boolean)
  const haystack = fold(label)
  return words.every(word => haystack.includes(word))
}

/** Meme libelle que celui renvoye par l'API : une edition porte sa date. */
export function programTargetLabel(program: { title: string; plannedFirstAirDate?: string | null }): string {
  const match = program.plannedFirstAirDate?.match(/^(\d{4})-(\d{2})-(\d{2})/)
  return match ? `${program.title} — ${match[3]}/${match[2]}/${match[1]}` : program.title
}
